import { create } from 'zustand';

export const SIDEBAR_STORAGE_KEY = 'veerny.sidebarCollapsed';
export const SIDEBAR_WIDTH = 280;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

/**
 * Read by hand rather than through zustand's persist: storage can throw (private windows,
 * blocked site data), and any failure has to mean "expanded", never a broken layout.
 */
const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeCollapsed = (collapsed: boolean) => {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed));
  } catch {
    // the toggle still applies to this page; it just will not survive a reload
  }
};

interface SidebarStore {
  /** Collapsed to the icon strip. Only ever changed by hand, never by screen width. */
  collapsed: boolean;
  toggleCollapsed: () => void;
  /**
   * Whether the Finance accordion is open. Kept here rather than in the accordion so it
   * survives the sidebar collapsing (which unmounts the accordion) and expanding again.
   * Not persisted: it reopens by itself whenever the route enters Finance.
   */
  financeOpen: boolean;
  setFinanceOpen: (open: boolean) => void;
}

export const useSidebarStore = create<SidebarStore>(set => ({
  collapsed: readCollapsed(),
  toggleCollapsed: () =>
    set(state => {
      writeCollapsed(!state.collapsed);
      return { collapsed: !state.collapsed };
    }),
  financeOpen: false,
  setFinanceOpen: open => set({ financeOpen: open })
}));

export const useSidebarWidth = (): number => useSidebarStore(state => (state.collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH));
